import {
  Box,
  Paper,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
  Skeleton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { TabContext, TabPanel } from '@mui/lab';
import { BaseButton } from 'components/BaseButton';
import clsx from 'clsx';
import { ListingEntry } from 'graphql/listings/queries';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PartyList, PartyItemList } from '../hooks/index';
import { filterPartiesByQuery } from '../utils';
import PartyItem from './PartyItem';
import PartyListRole from './PartyListRole';
import PartyNameFilter from './PartyNameFilter';
import TabScrollButton from './TabScrollButton';

const useStyles = makeStyles((theme: any) => ({
  paper: {
    border: `1px solid ${theme.palette.border.card}`,
    borderRadius: 4,
    boxShadow: 'none',
  },
  tabs: {
    '& .MuiTab-root': {
      textTransform: 'none',
      [theme.breakpoints.up('sm')]: {
        minWidth: 0,
        maxWidth: 'none',
        padding: theme.spacing(0.75, 2),
      },
    },
  },
  tabsSticky: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    backgroundColor: theme.palette.background.paper,
  },
  containerPanel: {
    padding: theme.spacing(1.5),
  },
  skeletonRow: {
    marginBottom: theme.spacing(1.5),
    borderRadius: 4,
  },
  state: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    padding: theme.spacing(2, 1.5),
  },
  stateTitle: {
    color: theme.palette.primary.darker,
  },
  stateLabel: {
    color: theme.palette.text.secondary,
  },
  empty: {
    color: theme.palette.text.secondary,
  },
}));

interface PartySideProps {
  partiesListRole: PartyList[];
  partiesListName: PartyItemList[];
  partiesListOthers: PartyList[];
  handleDeleteAssociation: (partyId: string, kind: string, onSuccess?: () => void) => void;
  listingEntries: ListingEntry[];
  handleViewChange: (id: string) => void;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  isEmpty?: boolean;
  selectedId?: string;
  isDeleting?: boolean;
}

const PartySide: FC<PartySideProps> = ({
  partiesListRole,
  partiesListName,
  partiesListOthers,
  handleDeleteAssociation,
  listingEntries,
  handleViewChange,
  isLoading = false,
  isError = false,
  onRetry,
  isEmpty = false,
  selectedId,
  isDeleting = false,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const [tabIndex, setTabIndex] = useState('role');
  const [nameFilter, setNameFilter] = useState('');

  const itemVariant = isDesktop ? 'row' : 'card';
  const filteredNameList = filterPartiesByQuery(partiesListName, nameFilter, selectedId);

  const handleChange = (_, tab: string) => {
    setTabIndex(tab);
  };

  const listSkeleton = (
    <Box>
      {[1, 2, 3, 4, 5].map((row) => (
        <Skeleton
          key={row}
          variant="rectangular"
          animation="wave"
          height={56}
          className={classes.skeletonRow}
        />
      ))}
    </Box>
  );

  const listState = isError ? (
    <Box className={classes.state}>
      <Typography variant="body2" className={classes.stateTitle}>
        {t('parties:load-error')}
      </Typography>
      {onRetry && (
        <BaseButton
          buttonVariant="outlined"
          buttonColor="inherit"
          icon="sync"
          text={t('common:retry')}
          onClick={onRetry}
          customStyle={{ minHeight: 40 }}
        />
      )}
    </Box>
  ) : (
    <Box className={classes.state}>
      <Typography variant="body2" className={classes.stateTitle}>
        {t('parties:no-parties-yet')}
      </Typography>
      <Typography variant="body2" className={classes.stateLabel}>
        {t('parties:no-parties-label')}
      </Typography>
    </Box>
  );

  const renderRoleList = (parties: PartyList[]) => {
    if (!parties.length) {
      return (
        <Typography variant="body2" className={classes.empty}>
          {t('parties:no-parties-group')}
        </Typography>
      );
    }

    return (
      <PartyListRole
        parties={parties}
        handleDeleteAssociation={handleDeleteAssociation}
        listingEntries={listingEntries}
        handleViewChange={handleViewChange}
        variant={itemVariant}
        selectedId={selectedId}
        isDeleting={isDeleting}
      />
    );
  };

  const renderNameList = () => (
    <>
      <PartyNameFilter value={nameFilter} onChange={setNameFilter} />
      {filteredNameList.length > 0 ? (
        <PartyItem
          parties={filteredNameList}
          handleDeleteAssociation={handleDeleteAssociation}
          handleViewChange={handleViewChange}
          variant={itemVariant}
          selectedId={selectedId}
          isDeleting={isDeleting}
        />
      ) : (
        <Box className={classes.state}>
          <Typography variant="body2" className={classes.stateTitle}>
            {nameFilter.trim()
              ? t('parties:no-filter-results')
              : t('parties:no-parties-group')}
          </Typography>
          {nameFilter.trim() && (
            <BaseButton
              buttonVariant="outlined"
              buttonColor="inherit"
              icon="times"
              text={t('common:clear')}
              onClick={() => setNameFilter('')}
              customStyle={{ minHeight: 40 }}
            />
          )}
        </Box>
      )}
    </>
  );

  const renderPanel = (content: React.ReactNode) => {
    if (isLoading) return listSkeleton;
    if (isError || isEmpty) return listState;

    return content;
  };

  return (
    <Paper variant="outlined" className={classes.paper}>
      <TabContext value={tabIndex}>
        <Tabs
          value={tabIndex}
          indicatorColor="secondary"
          textColor="secondary"
          onChange={handleChange}
          variant="scrollable"
          scrollButtons="auto"
          ScrollButtonComponent={TabScrollButton}
          className={clsx(classes.tabs, isDesktop && classes.tabsSticky)}
          aria-label={t('parties:groups')}
        >
          <Tab value="role" label={t('orders:byRoleTab')} />
          <Tab value="name" label={t('orders:byNameTab')} />
          <Tab value="others" label={t('orders:othersTab')} />
        </Tabs>
        <TabPanel value="role" className={classes.containerPanel}>
          {renderPanel(renderRoleList(partiesListRole))}
        </TabPanel>
        <TabPanel value="name" className={classes.containerPanel}>
          {renderPanel(renderNameList())}
        </TabPanel>
        <TabPanel value="others" className={classes.containerPanel}>
          {renderPanel(renderRoleList(partiesListOthers))}
        </TabPanel>
      </TabContext>
    </Paper>
  );
};

export default PartySide;
